/**
 * useChatStream — 聊天流式响应 Hook
 *
 * 职责：
 * - 封装 SSE 流式响应的消费逻辑
 * - 管理流式响应状态（连接中/接收中/已完成/出错）
 * - 提供中止流式请求的能力
 * - 与 useChatMessages 的解耦：本 Hook 只处理网络层，不关心消息列表
 */

import { useState, useRef, useCallback } from 'react';
import { chatAPI } from '@/service/chat';
import type { SSEEventChunk } from '@/service/chat';
import { readSSEStream } from '../utils/streamParser';

/** 流式连接状态 */
export type StreamStatus = 'idle' | 'connecting' | 'streaming' | 'completed' | 'error';

/** useChatStream 配置项 */
export interface UseChatStreamOptions {
  /** 每收到一个事件块时的回调 */
  onChunk?: (chunk: SSEEventChunk) => void;
  /** 流完成时的回调 */
  onComplete?: () => void;
  /** 流出错时的回调 */
  onError?: (error: string) => void;
}

/** useChatStream 返回值类型 */
export interface UseChatStreamReturn {
  /** 当前流状态 */
  status: StreamStatus;
  /** 错误信息 */
  error: string | null;
  /**
   * 启动一个 SSE 流式请求
   * @param sessionId 会话 ID
   * @param content 消息内容
   * @param externalSignal 外部中断信号（可选），如 ChatInput 的 Stop 按钮触发
   */
  startStream: (sessionId: number, content: string, externalSignal?: AbortSignal) => Promise<void>;
  /** 中止当前流式请求 */
  abortStream: () => void;
  /** 重置状态 */
  reset: () => void;
}

/**
 * 管理 SSE 流式响应的 Hook
 * 专注于网络层的流式数据传输
 */
export const useChatStream = (
  options: UseChatStreamOptions = {}
): UseChatStreamReturn => {
  const { onChunk, onComplete, onError } = options;

  const [status, setStatus] = useState<StreamStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  /**
   * 启动 SSE 流式请求
   *
   * 流程：
   * 1. 创建 AbortController
   * 2. 调用 sendMessageStream 获取 Response
   * 3. 通过 readSSEStream 逐块读取
   * 4. 按事件类型分发
   */
  const startStream = useCallback(
    async (sessionId: number, content: string, externalSignal?: AbortSignal) => {
      // 如果外部信号已中止，直接返回
      if (externalSignal?.aborted) return;

      // 中止上一个未完成的请求
      abortRef.current?.abort();

      setStatus('connecting');
      setError(null);

      const controller = new AbortController();
      abortRef.current = controller;

      // 监听外部信号：当外部信号中止时，同步中止内部控制器
      const onExternalAbort = () => controller.abort();
      externalSignal?.addEventListener('abort', onExternalAbort, { once: true });

      try {
        // 发起流式请求（同时传递内部 signal 实现网络层中止）
        setStatus('streaming');

        const response = await chatAPI.sendMessageStream(sessionId, content, undefined, controller.signal);

        if (!response.ok) {
          throw new Error(`请求失败: ${response.status} ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error('响应体为空，不支持流式传输');
        }

        // 逐块读取 SSE 流
        const reader = response.body.getReader();

        await readSSEStream(
          reader,
          (chunk: SSEEventChunk) => {
            // 向上回调
            onChunk?.(chunk);

            if (chunk.type === 'error') {
              setError(chunk.error || 'AI 响应错误');
              setStatus('error');
              onError?.(chunk.error || 'AI 响应错误');
            }
          },
          controller.signal
        );

        // 检查是否被中止
        if (controller.signal.aborted) {
          setStatus('idle');
          return;
        }

        setStatus('completed');
        onComplete?.();
      } catch (err) {
        if (controller.signal.aborted) {
          setStatus('idle');
          return;
        }
        const msg = err instanceof Error ? err.message : '流式请求失败';
        setError(msg);
        setStatus('error');
        onError?.(msg);
      } finally {
        externalSignal?.removeEventListener('abort', onExternalAbort);
        abortRef.current = null;
      }
    },
    [onChunk, onComplete, onError]
  );

  /**
   * 中止当前流式请求
   */
  const abortStream = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus('idle');
  }, []);

  /**
   * 重置状态
   */
  const reset = useCallback(() => {
    abortStream();
    setStatus('idle');
    setError(null);
  }, [abortStream]);

  return {
    status,
    error,
    startStream,
    abortStream,
    reset,
  };
};
